import React from 'react';
import { useParams, useHistory, Redirect, useLocation } from 'react-router-dom';
import AppLayout from 'layouts/app-layout';
import { PageTitle, Loading, SkeletonText } from 'components/atoms';
import { useFetch } from 'hooks';
import { useConfig } from 'config';
import { useAuth } from 'auth';
import { useTerminologyConfig } from 'context/DataContext';
import { ThirdPartyEdit } from 'components/organisms/third-party-edit';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from 'services/applicationInsightsService';
import { Breadcrumbs } from 'components/molecules';
import { BreadcrumbItem } from 'components/molecules/breadcrumbs/types';
import { ThirdPartyRouteInfo } from 'types/router';

const ThirdPartyEditPage = () => {
  const { id } = useParams<{ id?: string }>();
  const { tokenInfo } = useAuth();
  const terminologyConfig = useTerminologyConfig();
  const { vantageWebApi } = useConfig();
  const history = useHistory();
  const location = useLocation<{
    mainThirdParty: ThirdPartyRouteInfo;
    linkedThirdParty: ThirdPartyRouteInfo;
  }>();
  const locationState = location.state;
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${tokenInfo.accessToken}`,
  };

  const onComplete = () => {
    history.push(`/third-party/profile/${id}`);
  };

  const getBreadcrumb = (): BreadcrumbItem[] => {
    if (locationState?.linkedThirdParty) {
      return [
        {
          label: terminologyConfig?.thirdPartySingularNaming + ' manager',
          route: '/',
        },
        {
          label: locationState.mainThirdParty.name,
          route: `/third-party/profile/${locationState.mainThirdParty.id}`,
        },
        {
          label: locationState.linkedThirdParty.name,
          route: `/third-party/profile/${locationState.linkedThirdParty.id}`,
        },
        {
          label: `Edit ${locationState.linkedThirdParty.name} Details`,
        },
      ] as BreadcrumbItem[];
    } else {
      return [
        {
          label: terminologyConfig?.thirdPartySingularNaming + ' manager',
          route: '/',
        },
        {
          label: data?.name,
          route: `/third-party/profile/${id}`,
        },
        {
          label: `Edit ${data?.name} Details`,
        },
      ] as BreadcrumbItem[];
    }
  };

  const { data, loading } = useFetch(`${vantageWebApi}/thirdParty/${id}`, {
    method: 'GET',
    headers,
  });

  if (data?.status === 404) {
    return <Redirect to="/not-found" />;
  }
  if (data?.status === 401) {
    return <Redirect to="/unauthorised" />;
  }
  return (
    <AppLayout topBarSlot={loading ? <SkeletonText /> : <Breadcrumbs items={getBreadcrumb()} />}>
      {loading || !data ? (
        <Loading />
      ) : (
        <>
          <PageTitle title={data?.name ? `Third party: ${data?.name}` : 'Third party'} />
          <ThirdPartyEdit
            onCancel={onComplete}
            onSave={onComplete}
            id={id}
            thirdParty={data}
            inModal={false}
          />
        </>
      )}
    </AppLayout>
  );
};

export default withAITracking(reactPlugin, ThirdPartyEditPage);
